'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Button from '../../common/Button';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../../HOC/Modal';
import BoardTargetPicker, { type MoveTarget, soleMoveTarget } from './BoardTargetPicker';
type Props = {
  post: Post;
  closeHandler: () => void;
};

/** "Move to Board" in a card's menu: the same card leaves this board for a
 *  list of another one. The board room's socket events take the card off this
 *  board and put it on the other, so no cache is patched here. */
const MoveCardToBoardModal = t0 => {
  const $ = _c(37);
  const {
    post,
    closeHandler
  } = t0;
  const [picked, setTarget] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  let t1;
  let t2;
  if ($[0] !== post.space) {
    t1 = ["boardMoveTargets", post.space];
    t2 = () => unwrapFromResult(client.board.moveTargets.query({
      spaceSlug: post.space || ""
    }));
    $[0] = post.space;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = Boolean(post.space);
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data
  } = useQuery(t4);
  let t5;
  if ($[7] !== data?.boards || $[8] !== picked) {
    t5 = picked ?? soleMoveTarget(data?.boards);
    $[7] = data?.boards;
    $[8] = picked;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const target = t5;
  let t6;
  if ($[10] !== closeHandler || $[11] !== post.id || $[12] !== target) {
    t6 = async () => {
      if (!target) {
        return;
      }
      setIsMoving(true);
      const [error] = await client.board.moveCardToBoard.mutate({
        postId: post.id,
        toSpaceSlug: target.slug,
        toColumnId: target.listId
      });
      setIsMoving(false);
      if (error) {
        return toast(error.message, "", "error");
      }
      toast("Card moved");
      closeHandler();
    };
    $[10] = closeHandler;
    $[11] = post.id;
    $[12] = target;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const move = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2>Move to Board</h2>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== closeHandler) {
    t8 = <ModalHeader closeHandler={closeHandler}>{t7}</ModalHeader>;
    $[15] = closeHandler;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = data?.boards;
  let t10;
  if ($[17] !== t9 || $[18] !== target) {
    t10 = <BoardTargetPicker boards={t9} value={target} onChange={setTarget} />;
    $[17] = t9;
    $[18] = target;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="mt-4 text-sm text-textSecondary">The card keeps its link, comments, files and tagged members. Tags that belong to this board are removed. An AI session on the card, and its worktree and branch, stay on this board's runner.</p>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10) {
    t12 = <ModalContent>{t10}{t11}</ModalContent>;
    $[21] = t10;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== closeHandler) {
    t13 = <Button text="Cancel" variant="SECONDARY" onClick={closeHandler} />;
    $[23] = closeHandler;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !target || isMoving;
  let t15;
  if ($[25] !== isMoving || $[26] !== move || $[27] !== t14) {
    t15 = <Button text="Move" onClick={move} disabled={t14} loading={isMoving} />;
    $[25] = isMoving;
    $[26] = move;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t13 || $[30] !== t15) {
    t16 = <ModalFooter>{t13}{t15}</ModalFooter>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== closeHandler || $[33] !== t12 || $[34] !== t16 || $[35] !== t8) {
    t17 = <Modal closeHandler={closeHandler} maxWidth={500}>{t8}{t12}{t16}</Modal>;
    $[32] = closeHandler;
    $[33] = t12;
    $[34] = t16;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
};
export default MoveCardToBoardModal;